import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { CommonPipes } from 'app/trello/common-pipes.imports';
import { SparklineChartComponent } from 'app/trello/components/shared/visualisations/sparkline-chart/sparkline-chart.component';
import { Total } from 'app/trello/models/board-summary';
import { ChartableData, LabelledValues } from 'app/trello/models/charts';
import { PrimeNg } from 'app/trello/primeng.imports';
import { Templating } from 'app/trello/templating.imports';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonPipes,
    PrimeNg,
    Templating,
    SparklineChartComponent,
  ],
  selector: 'app-totals-insight-panel',
  styleUrls: ['./totals-insight-panel.component.scss'],
  templateUrl: './totals-insight-panel.component.html',
})
export class TotalsInsightPanelComponent<T> implements OnChanges, AfterViewInit {

  @Input() chartData: ChartableData<LabelledValues, never> | undefined;

  @Input() title!: string;
  @Input() icon!: string;
  @Input() totals!: Total<T>;
  @Input() comparableTotals?: Total<T>;
  @Input() lessIsMore: boolean = false;
  @Input() tiled: boolean = true;

  private initializedView: boolean = false;

  protected indicatorColor!: string;

  protected mappedChartData: ChartableData<LabelledValues, never> | undefined;

  public ngAfterViewInit(): void {

    this.initializedView = true;

    this.prepareData();
  }

  public ngOnChanges(
    changes: SimpleChanges,
  ): void {

    if (changes['chartData']) {
      this.prepareData();
    }
  }

  protected count(
    obj: any,
  ): number {

    if (Array.isArray(obj)) {
      return obj.length;

    } else if (obj && typeof obj === 'object') {
      return Object.keys(obj).length;
    }

    return 0;
  }

  protected negativeDifference(
    obj1: any,
    obj2: any,
  ): boolean {
    return (this.count(obj1) - this.count(obj2)) < 0;
  }

  protected noDifference(
    obj1: any,
    obj2: any,
  ): boolean {
    return (this.count(obj1) - this.count(obj2)) === 0;
  }

  protected positiveDifference(
    obj1: any,
    obj2: any,
  ): boolean {
    return (this.count(obj1) - this.count(obj2)) > 0;
  }

  private prepareData(): void {

    if (!this.initializedView) {
      return;
    }

    const deltaClasses = this.getDeltaClasses();

    this.indicatorColor = this.computeColorOffScreen(deltaClasses);

    if (!this.chartData) {

      this.mappedChartData = undefined;

      return;
    }

    this.mappedChartData = {
      ...this.chartData,
      secondaryColor: this.indicatorColor,
    };
  }

  private computeColorOffScreen(
    classes: string[],
  ): string {

    const dummyHost = document.createElement('app-totals-insight-panel');

    dummyHost.style.position = 'absolute';
    dummyHost.style.left = '-9999px';
    dummyHost.style.visibility = 'hidden';

    const dummyParagraph = document.createElement('p');

    dummyParagraph.style.position = 'absolute';
    dummyParagraph.style.left = '-9999px';
    dummyParagraph.style.visibility = 'hidden';

    dummyParagraph.classList.add('delta', ...classes);

    dummyHost.appendChild(dummyParagraph);

    document.body.appendChild(dummyHost);

    const color = getComputedStyle(dummyParagraph).color;

    document.body.removeChild(dummyHost);

    return color;
  }

  private getDeltaClasses(): string[] {

    const classes = [];

    if (this.lessIsMore) {
      classes.push('opposite');
    }

    if (this.negativeDifference(this.totals.end, this.totals.start)) {
      classes.push('negative');

    } else if (this.noDifference(this.totals.end, this.totals.start)) {
      classes.push('neutral');

    } else if (this.positiveDifference(this.totals.end, this.totals.start)) {
      classes.push('positive');
    }

    return classes;
  }

}
